import React, { useEffect, useState } from "react";
import { Tv, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Tiny client-side cache so we don't hit the API for the same title twice.
 * Keyed by "movie_872585" / "tv_100088". Lives for the page session only.
 */
const _cache = new Map();

/**
 * Hook: returns { available, qualities, seasons, count } for a given title,
 * batched & cached so a wall of poster cards on a grid page makes just one
 * round-trip every paint.
 */
export function useTvAvailability(tmdbId, mediaType) {
  const [state, setState] = useState(() => {
    const key = `${mediaType}_${tmdbId}`;
    return _cache.get(key) || { loading: true };
  });

  useEffect(() => {
    if (!tmdbId || !mediaType) return;
    const key = `${mediaType}_${tmdbId}`;
    const hit = _cache.get(key);
    if (hit && !hit.loading) {
      setState(hit);
      return;
    }
    let alive = true;
    api.get("/library/availability/single",
            { params: { tmdb_id: tmdbId, media_type: mediaType } })
       .then((r) => {
         const value = r.data || { available: false };
         _cache.set(key, value);
         if (alive) setState(value);
       })
       .catch(() => {
         const value = { available: false };
         _cache.set(key, value);
         if (alive) setState(value);
       });
    return () => { alive = false; };
  }, [tmdbId, mediaType]);

  return state;
}

/**
 * Compact glowing pill — "ON TORHUB TV · 1080p · 2160p"
 * Renders nothing while loading or if the title isn't in the library.
 */
export default function TvAvailableBadge({ tmdbId, mediaType, className = "", showQualities = true }) {
  const { available, qualities, seasons } = useTvAvailability(tmdbId, mediaType);
  if (!available) return null;
  const qLabel = (qualities || [])
    .filter(Boolean)
    .map((q) => q.toUpperCase())
    .join(" · ");
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full
                  bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] border border-[var(--tt-primary)]/40
                  shadow-[0_0_18px_-6px_var(--tt-primary)] ${className}`}
      data-testid={`tv-available-badge-${mediaType}-${tmdbId}`}
      title={mediaType === "tv" && seasons?.length ? `${seasons.length} season(s) on TorHub TV` : "Streaming on TorHub TV"}
    >
      <Tv className="w-3 h-3" />
      <span>On TorHub TV</span>
      {showQualities && qLabel && (
        <span className="font-mono-tt text-[var(--tt-primary-strong)] opacity-80">· {qLabel}</span>
      )}
    </span>
  );
}

/**
 * Card overlay variant — small icon-only chip for poster grids.
 * Use when you don't have room for the wordmark.
 */
export function TvAvailableDot({ tmdbId, mediaType, className = "" }) {
  const { available } = useTvAvailability(tmdbId, mediaType);
  if (!available) return null;
  return (
    <span
      className={`inline-flex items-center justify-center w-6 h-6 rounded-full
                  bg-[var(--tt-primary)] text-white shadow-[0_0_12px_-2px_var(--tt-primary)]
                  ${className}`}
      title="Available on TorHub TV"
      data-testid={`tv-available-dot-${mediaType}-${tmdbId}`}
    >
      <Tv className="w-3 h-3" />
    </span>
  );
}

/**
 * Episode-coverage strip for TV shows. Renders nothing for movies, while
 * loading, or if the show has zero owned episodes.
 * Shape: "S1 9/9 · S2 22/22 · S3 5/12" with each season a tiny pill that
 * goes green when complete, amber when partial.
 */
export function TvCoverageStrip({ tmdbId, mediaType, className = "" }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (mediaType !== "tv" || !tmdbId) return;
    let alive = true;
    api.get("/library/tv/coverage", { params: { tmdb_id: tmdbId } })
       .then((r) => { if (alive) setData(r.data); })
       .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [tmdbId, mediaType]);

  if (mediaType !== "tv" || !data || !data.total_owned) return null;
  const ratio = data.total_episodes
    ? Math.round((data.total_owned / data.total_episodes) * 100)
    : 100;
  const incomplete = data.total_owned < data.total_episodes;

  const requestMissing = async () => {
    try {
      await api.post("/library/requests",
        { tmdb_id: tmdbId, media_type: "tv",
          note: `Missing episodes: only ${data.total_owned}/${data.total_episodes} on TorHub TV` });
      toast.success("Thanks — we'll prioritise filling the gaps.");
    } catch {
      toast.error("Could not send request");
    }
  };

  return (
    <div
      className={`flex items-center gap-2 flex-wrap text-[11px] ${className}`}
      data-testid={`tv-coverage-strip-${tmdbId}`}
    >
      <span className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] font-bold">
        On TorHub TV
      </span>
      <span className="font-mono-tt text-[var(--tt-primary)] font-bold">
        {data.total_owned}/{data.total_episodes} eps · {ratio}%
      </span>
      <div className="flex items-center gap-1.5 flex-wrap">
        {data.seasons.map((s) => {
          const complete = s.total > 0 && s.owned >= s.total;
          const partial = s.owned > 0 && !complete;
          const color = complete
            ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
            : partial
              ? "bg-[var(--tt-yellow)]/15 text-[var(--tt-yellow)] border-[var(--tt-yellow)]/30"
              : "bg-white/5 text-[var(--tt-text-faint)] border-[var(--tt-border)]";
          return (
            <span
              key={s.season}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-mono-tt text-[10px] font-bold ${color}`}
              title={`Season ${s.season}: ${s.owned}/${s.total} episodes`}
              data-testid={`tv-coverage-season-${tmdbId}-${s.season}`}
            >
              S{s.season} {s.owned}/{s.total}
            </span>
          );
        })}
      </div>
      {incomplete && (
        <button
          onClick={requestMissing}
          data-testid={`tv-coverage-request-${tmdbId}`}
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[var(--tt-primary)] hover:text-[var(--tt-primary-strong)] underline underline-offset-2 ml-1"
        >
          <Mail className="w-3 h-3" /> Request missing
        </button>
      )}
    </div>
  );
}
